import { Injectable } from '@angular/core';
import { Empresa, IEmpresa } from '../../models/empresa.model';
import { AuthService } from '../auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from '../spinner/spinner.service';
import { ToastService } from '../toast/toast.service';
import { environment } from '../../../../environments/environment';
import { IServerResponse, ServerResponse } from '../../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class EmpresaService {

  public loading: boolean = false;
  public empresas: Empresa[] = [];
  public empresaCurrent: Empresa | null = null;

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) {
    this.getAll();
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR create                               */
  /* -------------------------------------------------------------------------- */
  // region create
  async create(
    empresa: any
  ): Promise<boolean> {

    let flag = false;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando empresa...';

    const urlApi = `${environment.urlApi}/empresas`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify(empresa),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 201) {

        flag = true;

        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white'
        });

        this.getAll();

      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR delete                               */
  /* -------------------------------------------------------------------------- */
  // region delete
  async delete(
    empresa: Empresa
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/empresas/${empresa.idEmpresa}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {

        flag = true;

        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white'
        });

        this.getAll();

      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR get                                 */
  /* -------------------------------------------------------------------------- */
  // region get
  async get(
    idEmpresa: string
  ): Promise<Empresa | null> {

    const urlApi = `${environment.urlApi}/empresas/${idEmpresa}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ empresa: IEmpresa }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ empresa: IEmpresa }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ empresa: IEmpresa }>
            ) => resolve(
              new ServerResponse<{ empresa: IEmpresa }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {

        this.empresaCurrent = new Empresa(
          response.data!.empresa
        );

      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    return this.empresaCurrent;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll() {

    this.loading = true;

    const urlApi = `${environment.urlApi}/empresas`;

    try {
      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ empresas: IEmpresa[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ empresas: IEmpresa[] }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ empresas: IEmpresa[] }>
            ) => resolve(
              new ServerResponse<{ empresas: IEmpresa[] }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {
        this.empresas = response.data!.empresas.map(
          empresa => new Empresa(empresa)
        );

      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR update                               */
  /* -------------------------------------------------------------------------- */
  // region update
  async update(
    empresa: any,
    showSpinner: boolean = true
  ): Promise<boolean> {

    // console.log(empresa);

    if (showSpinner) {
      this.spinnerService.loading = true;
      this.spinnerService.message = 'Actualizando empresa...';
    }

    let flag = false;

    const urlApi = `${environment.urlApi}/empresas/${empresa.idEmpresa}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(empresa),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(
              new ServerResponse(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {

        flag = true;

        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white'
        });

        this.getAll();

      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }
}
